/**
 * Broker Priority Configuration Page
 * 
 * Admin interface for configuring broker data source priority rankings
 * 
 * Features:
 * - Visual priority matrix (Exchange × Instrument Type)
 * - Drag-and-drop broker ranking
 * - Bulk priority updates
 * - Priority rule creation and deletion
 * 
 * Access: Admin-only (requires 'admin' scope)
 */

import React, { useState, useEffect } from 'react';
import { services } from '../../../services/api';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  FiArrowUp, 
  FiArrowDown, 
  FiPlus, 
  FiTrash2, 
  FiSave,
  FiRefreshCw,
  FiAlertCircle
} from 'react-icons/fi';

const BrokerPriorityConfig = ({ isEmbedded = false }) => {
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [priorityMatrix, setPriorityMatrix] = useState({});
  const [brokers, setBrokers] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [instrumentTypes, setInstrumentTypes] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      showSnackbar('Access denied. Admin privileges required.', 'error');
    }
  }, [user, showSnackbar]);

  // Fetch priority matrix on mount
  useEffect(() => {
    fetchPriorityMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPriorityMatrix = async () => {
    try {
      setLoading(true);
      const response = await services.instruments.admin.getPriorityMatrix();
      
      if (response.data && response.data.status === 'success') {
        const { matrix, brokers: brokerList, exchanges: exchangeList, instrument_types } = response.data.data;
        
        setPriorityMatrix(matrix || {});
        setBrokers(brokerList || []);
        setExchanges(exchangeList || []);
        setInstrumentTypes(instrument_types || []);
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error fetching priority matrix:', error);
      showSnackbar(error.message || 'Failed to load priority configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Move broker up in priority (decrease rank number)
  const moveBrokerUp = (exchange, instrumentType, brokerIndex) => {
    if (brokerIndex === 0) return; // Already at top

    const key = `${exchange}_${instrumentType}`;
    const currentRules = [...(priorityMatrix[key] || [])];
    
    // Swap with previous broker
    [currentRules[brokerIndex - 1], currentRules[brokerIndex]] = 
    [currentRules[brokerIndex], currentRules[brokerIndex - 1]];
    
    // Update ranks
    currentRules.forEach((rule, idx) => {
      rule.priority_rank = idx + 1;
    });
    
    setPriorityMatrix({
      ...priorityMatrix,
      [key]: currentRules
    });
    setHasChanges(true);
  };

  // Move broker down in priority (increase rank number)
  const moveBrokerDown = (exchange, instrumentType, brokerIndex) => {
    const key = `${exchange}_${instrumentType}`;
    const currentRules = priorityMatrix[key] || [];
    
    if (brokerIndex === currentRules.length - 1) return; // Already at bottom

    const updatedRules = [...currentRules];
    
    // Swap with next broker
    [updatedRules[brokerIndex], updatedRules[brokerIndex + 1]] = 
    [updatedRules[brokerIndex + 1], updatedRules[brokerIndex]];
    
    // Update ranks
    updatedRules.forEach((rule, idx) => {
      rule.priority_rank = idx + 1;
    });
    
    setPriorityMatrix({
      ...priorityMatrix,
      [key]: updatedRules
    });
    setHasChanges(true);
  };

  // Add new broker to priority list
  const addBroker = (exchange, instrumentType, brokerId) => {
    const key = `${exchange}_${instrumentType}`;
    const currentRules = priorityMatrix[key] || [];
    
    // Check if broker already exists
    if (currentRules.some(rule => rule.broker_id === brokerId)) {
      showSnackbar('This broker is already in the priority list', 'warning');
      return;
    }

    const newRule = {
      broker_id: brokerId,
      exchange: exchange,
      instrument_type: instrumentType,
      priority_rank: currentRules.length + 1,
      is_active: true,
      segment: null
    };

    setPriorityMatrix({
      ...priorityMatrix,
      [key]: [...currentRules, newRule]
    });
    setHasChanges(true);
  };

  // Remove broker from priority list
  const removeBroker = (exchange, instrumentType, brokerIndex) => {
    const key = `${exchange}_${instrumentType}`;
    const currentRules = [...(priorityMatrix[key] || [])];
    
    currentRules.splice(brokerIndex, 1);
    
    // Re-number remaining rules
    currentRules.forEach((rule, idx) => {
      rule.priority_rank = idx + 1;
    });
    
    setPriorityMatrix({
      ...priorityMatrix,
      [key]: currentRules
    });
    setHasChanges(true);
  };

  // Toggle broker active status
  const toggleBrokerActive = (exchange, instrumentType, brokerIndex) => {
    const key = `${exchange}_${instrumentType}`;
    const currentRules = [...(priorityMatrix[key] || [])];
    
    currentRules[brokerIndex].is_active = !currentRules[brokerIndex].is_active;
    
    setPriorityMatrix({
      ...priorityMatrix,
      [key]: currentRules
    });
    setHasChanges(true);
  };

  // Save all changes
  const saveChanges = async () => {
    try {
      setSaving(true);

      // Prepare bulk update payload
      const updates = [];
      
      Object.entries(priorityMatrix).forEach(([key, rules]) => {
        rules.forEach(rule => {
          updates.push({
            broker_id: rule.broker_id,
            exchange: rule.exchange,
            instrument_type: rule.instrument_type,
            segment: rule.segment,
            priority_rank: rule.priority_rank,
            is_active: rule.is_active
          });
        });
      });

      const response = await services.instruments.admin.bulkUpdatePriorities(updates);
      
      if (response.data && response.data.status === 'success') {
        showSnackbar('Priority configuration saved successfully', 'success');
        setHasChanges(false);
        fetchPriorityMatrix(); // Refresh to get updated data
      }
    } catch (error) {
      console.error('Error saving priority configuration:', error);
      showSnackbar(error.message || 'Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Get broker name by ID
  const getBrokerName = (brokerId) => {
    const broker = brokers.find(b => b.id === brokerId);
    return broker ? broker.name : brokerId;
  };

  // Get available brokers for dropdown
  const getAvailableBrokers = (exchange, instrumentType) => {
    const key = `${exchange}_${instrumentType}`;
    const currentRules = priorityMatrix[key] || [];
    const usedBrokerIds = currentRules.map(rule => rule.broker_id);
    
    return brokers.filter(broker => !usedBrokerIds.includes(broker.id));
  };

  // Priority badge color based on rank
  const getPriorityColor = (rank) => {
    if (rank === 1) return 'bg-green-100 text-green-800 border-green-300';
    if (rank === 2) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (rank === 3) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-bg-3 text-fg-1 border-[var(--divider)]';
  };

  if (loading) {
    const content = (
      <div className="flex items-center justify-center h-64">
        <FiRefreshCw className="animate-spin text-4xl text-blue-500" />
        <span className="ml-3 text-lg">Loading priority configuration...</span>
      </div>
    );
    
    return isEmbedded ? content : (
      <div className="m-2 md:m-6 p-4 md:p-8 bg-bg-2 rounded-3xl">
        {content}
      </div>
    );
  }

  if (user && user.role !== 'admin') {
    const content = (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
        <FiAlertCircle className="text-red-600 dark:text-red-400 text-xl flex-shrink-0" />
        <div>
          <p className="text-red-800 dark:text-red-200 font-semibold">Admin Access Required</p>
          <p className="text-red-600 dark:text-red-400 text-sm">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
    
    return isEmbedded ? content : (
      <div className="m-2 md:m-6 p-4 md:p-8 bg-bg-2 rounded-3xl">
        {content}
      </div>
    );
  }

  const mainContent = (
    <>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="ds-h1 tracking-tight text-slate-900 dark:text-gray-200">
            Broker Priority Configuration
          </h1>
          <p className="text-fg-2 mt-2">
            Configure broker data source priority rankings for instrument reconciliation
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            type="button"
            onClick={fetchPriorityMatrix}
            disabled={saving}
            className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <FiRefreshCw className={saving ? 'animate-spin' : ''} />
            Refresh
          </button>

          <button
            type="button"
            onClick={saveChanges}
            disabled={!hasChanges || saving}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Change indicator */}
      {hasChanges && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-yellow-800 dark:text-yellow-200 text-sm">
            ⚠️ You have unsaved changes. Click "Save Changes" to apply them.
          </p>
        </div>
      )}

      {/* Priority Matrix Grid */}
      <div className="space-y-6">
        {exchanges.map(exchange => (
          <div key={exchange} className="border border-[var(--divider)] rounded-lg overflow-hidden">
            {/* Exchange Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-3">
              <h2 className="ds-h2 text-white">{exchange}</h2>
            </div>

            {/* Instrument Types Grid */}
            <div className="p-6 space-y-6">
              {instrumentTypes.map(instrumentType => {
                const key = `${exchange}_${instrumentType}`;
                const rules = priorityMatrix[key] || [];
                const availableBrokers = getAvailableBrokers(exchange, instrumentType);

                return (
                  <div key={instrumentType} className="border border-[var(--divider)] rounded-lg p-4">
                    {/* Instrument Type Header */}
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="ds-h3 text-fg-1">
                        {instrumentType}
                      </h3>
                      
                      {/* Add Broker Dropdown */}
                      {availableBrokers.length > 0 && (
                        <div className="flex items-center gap-2">
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                addBroker(exchange, instrumentType, e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="px-3 py-2 border border-[var(--divider)] rounded-lg 
                                     bg-white dark:bg-bg-3 text-fg-1"
                          >
                            <option value="">Add Broker...</option>
                            {availableBrokers.map(broker => (
                              <option key={broker.id} value={broker.id}>
                                {broker.name}
                              </option>
                            ))}
                          </select>
                          <FiPlus className="text-blue-500" />
                        </div>
                      )}
                    </div>

                    {/* Broker Priority List */}
                    {rules.length === 0 ? (
                      <div className="text-center py-8 text-fg-2">
                        No brokers configured for this combination. Add a broker to get started.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {rules.map((rule, index) => (
                          <div
                            key={rule.broker_id}
                            className={`
                              flex items-center justify-between p-4 rounded-lg border-2 transition-all
                              ${rule.is_active 
                                ? getPriorityColor(rule.priority_rank) 
                                : 'bg-bg-3 text-fg-3 border-[var(--divider)] opacity-60'
                              }
                            `}
                          >
                            {/* Rank Badge */}
                            <div className="flex items-center gap-4">
                              <div className="text-2xl font-bold w-12 text-center">
                                #{rule.priority_rank}
                              </div>
                              
                              {/* Broker Name */}
                              <div>
                                <div className="font-semibold text-lg">
                                  {getBrokerName(rule.broker_id)}
                                </div>
                                <div className="text-xs opacity-75">
                                  {rule.is_active ? 'Active' : 'Inactive'}
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2">
                              {/* Move Up */}
                              <button
                                onClick={() => moveBrokerUp(exchange, instrumentType, index)}
                                disabled={index === 0}
                                className="p-2 hover:bg-white dark:hover:bg-bg-3 rounded transition-colors 
                                         disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move Up"
                              >
                                <FiArrowUp className="text-lg" />
                              </button>

                              {/* Move Down */}
                              <button
                                onClick={() => moveBrokerDown(exchange, instrumentType, index)}
                                disabled={index === rules.length - 1}
                                className="p-2 hover:bg-white dark:hover:bg-bg-3 rounded transition-colors 
                                         disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move Down"
                              >
                                <FiArrowDown className="text-lg" />
                              </button>

                              {/* Toggle Active */}
                              <button
                                onClick={() => toggleBrokerActive(exchange, instrumentType, index)}
                                className="px-3 py-1 bg-white dark:bg-bg-3 rounded border border-current 
                                         hover:bg-bg-3 dark:hover:bg-bg-3 transition-colors text-sm"
                              >
                                {rule.is_active ? 'Disable' : 'Enable'}
                              </button>

                              {/* Remove */}
                              <button
                                onClick={() => removeBroker(exchange, instrumentType, index)}
                                className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 
                                         dark:text-red-400 rounded transition-colors"
                                title="Remove"
                              >
                                <FiTrash2 className="text-lg" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">How Priority Works:</h4>
        <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-1 list-disc list-inside">
          <li><strong>Rank #1</strong> (Green): Highest priority - this broker's data is used first</li>
          <li><strong>Rank #2</strong> (Blue): Second priority - used if #1 unavailable or conflicts</li>
          <li><strong>Rank #3+</strong> (Yellow/Gray): Lower priority - used as fallback</li>
          <li><strong>Inactive brokers</strong> are skipped during reconciliation</li>
          <li>Use arrows to reorder, or drag-and-drop when available</li>
        </ul>
      </div>
    </>
  );

  return isEmbedded ? mainContent : (
    <div className="m-2 md:m-6 p-4 md:p-8 bg-bg-2 rounded-3xl">
      {mainContent}
    </div>
  );
};

export default BrokerPriorityConfig;
